// #336 slice 5, decision 4 (§5.2.4, §11.3): the rounds already done in a
// program in progress are not written. A session reads its prescription back
// from the program, so a change there would rewrite what the history says was
// prescribed. Reading them, copying from them and writing the later rounds
// stay free. Keep free of React and any UI import.
import type { Draft, SlotDraft } from "./programEdit.js";
import { isCellAbsent, kindAt } from "./programEditBuild.js";
import { draftRoundTypes } from "./programProgressionApply.js";

/** What slot `s` prescribes in round `w`, as one comparable string. Notes and
 *  names stay out: a language switch rewrites them and prescribes nothing.
 *  The axis is the effective one (kindAt): a rule that moves the row to
 *  another axis writes the old one on each done cell, and prescribes the same. */
const roundSig = (s: SlotDraft, w: number, day: string): string => {
  if (s.inactiveWeeks[w]) return `${day}|off`;
  const sets: unknown[] = [];
  for (const row of s.setRows) {
    if (isCellAbsent(row, w)) break;
    const cell: Record<string, unknown> | null = row.cells[w] ? { ...row.cells[w] } : null;
    if (cell) delete cell.kind;
    sets.push([cell, kindAt(row, w), row.workKind ?? ""]);
  }
  const variants = s.variantsRaw.map((v) => ({ ...v, exerciseName: undefined }));
  return JSON.stringify([day, s.exerciseId, s.amrapLastSet, s.maxAttempt, variants, sets]);
};

const runsIn = (s: SlotDraft, through: number): boolean =>
  s.inactiveWeeks.slice(0, through + 1).some((off) => !off);

/** `next` when it leaves rounds 0..lockedThrough as `prev` has them, with any
 *  new exercise kept out of those rounds; null when it would change them (a
 *  cell, a set, a variant, the exercise, a round moved or taken out, an
 *  exercise or a day that runs there removed, or such a day moved: a session
 *  saved before MC-8 finds its day by position). */
export const guardLockedRounds = (prev: Draft, next: Draft, lockedThrough: number): Draft | null => {
  if (lockedThrough < 0 || prev === next) return next;
  const L = Math.min(lockedThrough, prev.cycleWeeks - 1);
  if (next.cycleWeeks <= L) return null;
  const oldTypes = draftRoundTypes(prev);
  const newTypes = draftRoundTypes(next);
  for (let w = 0; w <= L; w += 1) if (oldTypes[w] !== newTypes[w]) return null;
  const dayAt = new Map(next.sessions.map((d, i) => [d.id, i]));
  for (const [i, d] of prev.sessions.entries()) {
    if (d.slots.some((s) => runsIn(s, L)) && dayAt.get(d.id) !== i) return null;
  }
  const after = new Map(next.sessions.flatMap((d) => d.slots.map((s) => [s.id, { s, day: d.id }] as const)));
  for (const d of prev.sessions) {
    for (const s of d.slots) {
      const b = after.get(s.id);
      if (!b) {
        if (runsIn(s, L)) return null;
        continue;
      }
      if (b.s === s && b.day === d.id) continue;
      for (let w = 0; w <= L; w += 1) if (roundSig(s, w, d.id) !== roundSig(b.s, w, b.day)) return null;
    }
  }
  const known = new Set(prev.sessions.flatMap((d) => d.slots.map((s) => s.id)));
  let changed = false;
  let nowhere = false;
  const sessions = next.sessions.map((d) => {
    const slots = d.slots.map((s) => {
      if (known.has(s.id) || !runsIn(s, L)) return s;
      changed = true;
      const inactiveWeeks = s.inactiveWeeks.map((off, w) => off || w <= L);
      // Every round done: the new exercise would run nowhere.
      if (inactiveWeeks.every(Boolean)) nowhere = true;
      return { ...s, inactiveWeeks };
    });
    return slots.some((s, i) => s !== d.slots[i]) ? { ...d, slots } : d;
  });
  if (nowhere) return null;
  return changed ? { ...next, sessions } : next;
};
